import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Warning, Fire, Users } from "@phosphor-icons/react";
import StatusBadge from "@/components/gate-check/StatusBadge";
import { toast } from "sonner";
import { fmtIST } from "@/lib/datetime";

export default function EmergencyView() {
    const [inside, setInside] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/gate-check/visits/currently-inside");
            setInside(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

    const exportList = () => {
        const rows = [["Name", "Phone", "Company", "Host", "Gate", "Vehicle", "Check-in"]];
        inside.forEach((v) => rows.push([
            v.visitor_name, v.visitor_phone, v.company || "", v.host_name || "",
            v.gate_name || "", v.vehicle_number || "", fmtIST(v.check_in_at) || "",
        ]));
        const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `emergency_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Emergency roster exported");
    };

    return (
        <div>
            <PageHeader
                overline="Live Occupancy"
                title="Emergency View"
                subtitle="All personnel currently on premises. Refreshes every 15 seconds."
                actions={
                    <Button onClick={exportList} data-testid="btn-emergency-export"
                        className="h-11 bg-red-600 hover:bg-red-700 text-white">
                        <Fire className="mr-2" size={16} weight="bold" /> Export Roster
                    </Button>
                }
            />

            <div className="p-6 space-y-6">
                <div className="rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Warning size={28} weight="fill" className="text-red-600" />
                        <div>
                            <div className="overline text-red-700 dark:text-red-400">Active Occupancy</div>
                            <div className="font-display text-4xl font-black tracking-tighter">
                                <span data-testid="occupancy-count">{inside.length}</span> Persons Inside
                            </div>
                        </div>
                    </div>
                    <Users size={48} weight="duotone" className="text-red-500/50 hidden sm:block" />
                </div>

                <Card className="tech-card overflow-hidden">
                    <div className="px-5 py-4 border-b border-border font-display font-bold text-lg tracking-tight">
                        Personnel Currently On Premises
                    </div>
                    <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                        {loading && <div className="p-6 text-center text-muted-foreground">Loading…</div>}
                        {!loading && inside.length === 0 && (
                            <div className="p-8 text-center text-muted-foreground">Premises are empty.</div>
                        )}
                        {inside.map((v) => (
                            <div key={v.id} data-testid={`emergency-row-${v.id}`}
                                className="px-5 py-4 flex items-center gap-4">
                                <div className="h-11 w-11 rounded-full bg-brand/10 text-brand flex items-center justify-center font-bold">
                                    {v.visitor_name?.[0]?.toUpperCase() || "?"}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold">{v.visitor_name} <span className="text-xs text-muted-foreground">· {v.visitor_phone}</span></div>
                                    <div className="text-xs text-muted-foreground">
                                        {v.company || "Individual"} · Host: <b>{v.host_name || "—"}</b> · Gate: {v.gate_name} · Vehicle: {v.vehicle_number || "—"}
                                    </div>
                                </div>
                                <StatusBadge status={v.status} />
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
}
